import React from 'react';
import _ from 'lodash';
import {
  Font,
  Page,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer';

import { OrganizationBrandData } from '../../../_libs/app/types';
import { ThemeDataType } from '../../../_libs/data/types';

import { LogoViewType } from './types';
import formatImageSource from '../utils/formatImageSource';
import getLogoDimensionsForView, { RectangularDimensions } from '../utils/getLogoDimensionsForView';
import radiusToText from '../utils/radiusToText';
import removeHyphenation from '../utils/removeHyphenation';

import PdfImageFromAzureWithOptions from '../../../atoms/PdfImageFromAzureWithOptions';
import PdfImageFromAzureWithUri from '../../../atoms/PdfImageFromAzureWithUri';

import QrCodeSvg from '../../../molecules/QrCodeSvg';
import { ZipCodeSvgAlt } from '../../../molecules/ZipCodeSvg';
import ErrorBoundary from '../../../organisms/ErrorBoundary';
interface InstagramType {
  isCoBranded?: boolean;
  coBrand?: OrganizationBrandData;
  lbBrand?: OrganizationBrandData;
  imgSrc?: string;
  primaryBrand?: OrganizationBrandData;
  radius?: number;
  themeData: ThemeDataType;
  themeImageSrc?: string;
  zipCode?: string;
  uuid?: string;
}

removeHyphenation(Font);

const Styles = StyleSheet.create({
  announcementView: {
    width: 555,
    height: 100,
    position: 'absolute',
    top: 36,
    left: 36,
  },
  announcement: {
    fontFamily: 'Courier',
    fontSize: '24pt',
    lineHeight: 1.17,
    textTransform: 'uppercase',
  },
  announcementTheme: {
    fontFamily: 'Times-Roman',
    fontSize: '48pt',
    position: 'absolute',
    bottom: 0,
  },
  areaView: {
    position: 'absolute',
    left: 36,
    bottom: 36,
    width: 555,
    fontFamily: 'Helvetica',
    fontSize: '24pt',
    lineHeight: 1.17,
  },
  areaViewAlt: {
    position: 'absolute',
    right: 36,
    bottom: 36,
    textAlign: 'right',
    fontFamily: 'Helvetica',
    fontSize: '24pt',
    lineHeight: 1.17,
  },
  areaEmphasis: {
    fontFamily: 'Times-Bold',
    fontSize: '28pt'
  },
  photo: {
    width: 432,
    height: 432,
    position: 'absolute',
    left: 36,
    top: 159,
    borderRadius: 216,
    backgroundColor: '#F0F',
    overflow: 'hidden',
  },
  textView: {
    width: 555,
    height: 414,
    position: 'absolute',
    top: 159,
    left: 36,
    display: 'flex',
    alignContent: 'center',
    justifyContent: 'flex-start',
    fontFamily: 'Times-Roman',
    fontSize: 52,
    lineHeight: 1,
  },
  textEmphasis: {
    fontFamily: 'Times-BoldItalic',
  },
  textStrong: {
    fontFamily: 'Times-Bold',
  },
  imageFit: {
    maxWidth: '100%',
    maxHeight: '100%',
  },
  imageFill: {
    minWidth: '100%',
    minHeight: '100%',
  },
  materialIdView: {
    position: 'absolute',
    left: 36,
    bottom: 36,
    opacity: 0.5,
  },
  materialId: {
    fontFamily: 'Courier',
    fontSize: '12pt'
  },
  brandView: {
    width: 555,
    height: 200,
    position: 'absolute',
    left: 36,
    top: 190,
  },
  brandLogo: {
    position: 'absolute',
    bottom: 0,
    width: 'auto',
    height: 165,
  },
  calloutView: {
    width: 150,
    height: 150,
    borderRadius: 75,
    backgroundColor: 'black',
    position: 'absolute',
    top: 183,
    left: 372,
    textAlign: 'center',
    display: 'flex',
    alignContent: 'center',
    justifyContent: 'center',
    transform: 'rotate(-5deg)',
  },
  calloutTitle: {
    fontFamily: 'Helvetica',
    fontSize: '24pt',
  },
  calloutZipCode: {
    fontFamily: 'Times-Bold',
    fontSize: '54pt',
  },
  calloutBg: {
  },
  calloutBgView: {
    width: 281,
    height: 279,
    transform: 'rotate(-5deg)',
    position: 'absolute',
    top: 124,
    left: 300,
  },
  qr: {
    width: 150,
    height: 150,
    position: 'absolute',
    top: 158,
    left: 406,
    transform: 'rotate(-5deg)',
  },
  qrBg: {
    width: 219,
    height: 234.5,
    position: 'absolute',
    top: 114,
    left: 376.5,
    transform: 'rotate(-5deg)',
  },
});

// Debug
// _.each(Styles, (style) => {
//   style['border'] = '1px solid magenta';
// });

const InstagramPage = ({
  children,
  style = null,
}): JSX.Element => {
  return (
    <Page size={[627, 627]} style={style}>
      {children}
    </Page>
  );
};

const Instagram01 = ({
  imgSrc,
  primaryBrand,
  radius,
  themeData,
  zipCode,
}: InstagramType): JSX.Element => {
  return (
    <InstagramPage style={{
      backgroundColor: primaryBrand.colors.dark,
      color: 'white',
    }}>
      <View style={Styles.announcementView}>
        <Text style={Styles.announcement}>An announcement about</Text>
        <Text style={Styles.announcementTheme}>{themeData.public_name}</Text>
      </View>
      <View style={Styles.calloutBgView}>
        <ZipCodeSvgAlt color={'#FFF'} scale={1.84} /> 
      </View>
      <View style={Styles.photo}>
        <PdfImageFromAzureWithOptions
          style={Styles.imageFit}
          fileName={imgSrc}
          folderPath="assets/people_portraits/"
        />
      </View>
      <View style={Styles.calloutView}>
        <Text style={Styles.calloutTitle}>ZIP Code</Text>
        <Text style={Styles.calloutZipCode}>{zipCode}</Text>
      </View>
      <View style={Styles.areaViewAlt}>
        <Text>
          For
          &#10;anyone
          &#10;living within
          &#10;<Text style={Styles.areaEmphasis}>{radiusToText(radius)}</Text> of
          &#10;ZIP code <Text style={Styles.areaEmphasis}>{zipCode}</Text>
        </Text>
      </View>
    </InstagramPage>
  );
};

const Instagram02 = ({
  primaryBrand,
  radius,
  themeData,
  zipCode,
}: InstagramType): JSX.Element => {
  return (
    <InstagramPage style={{
      backgroundColor: primaryBrand.colors.dark,
      color: 'white',
    }}>
      <View style={Styles.announcementView}>
        <Text style={Styles.announcement}>An announcement about</Text>
        <Text style={Styles.announcementTheme}>{themeData.public_name}</Text>
      </View>
      <View style={Styles.textView}>
        <Text>
          <Text style={Styles.textEmphasis}>{themeData.body_emphasis}</Text> {themeData.body}
        </Text>
      </View>
      <View style={Styles.areaView}>
        <Text>
          For residents within <Text style={Styles.areaEmphasis}>{radiusToText(radius)}</Text> of ZIP code <Text style={Styles.areaEmphasis}>{zipCode}</Text>
        </Text>
      </View>
    </InstagramPage>
  );
};

const Instagram03 = ({
  radius,
  themeData,
  zipCode,
}: InstagramType): JSX.Element => {
  return (
    <InstagramPage
      style={{
        color: 'black',
      }}
    >
      <View style={Styles.announcementView}>
        <Text style={Styles.announcement}>An announcement about</Text>
        <Text style={Styles.announcementTheme}>{themeData.public_name}</Text>
      </View>
      <View style={Styles.textView}>
        <Text style={Styles.textStrong}>
          {themeData.header}
        </Text>
      </View>
      <View style={Styles.areaView}>
        <Text>
          For residents within <Text style={Styles.areaEmphasis}>{radiusToText(radius)}</Text> of ZIP code <Text style={Styles.areaEmphasis}>{zipCode}</Text>
        </Text>
      </View>
    </InstagramPage>
  );
};

const Instagram04 = ({
  primaryBrand,
  themeData,
}: InstagramType): JSX.Element => {
  return (
    <InstagramPage
      style={{
        color: primaryBrand.colors.dark,
      }}
    >
      <View style={Styles.announcementView}>
        <Text style={Styles.announcement}>An announcement about</Text>
        <Text style={Styles.announcementTheme}>{themeData.public_name}</Text>
      </View>
      <View style={Styles.qrBg}>
        <QrCodeSvg color={primaryBrand.colors.primary} scale={1.60} />
      </View>
      <View style={Styles.photo}>
        <PdfImageFromAzureWithOptions
          style={Styles.imageFill}
          fileName={themeData.image_src}
          folderPath="assets/theme_images/"
        />
      </View>
      <View style={Styles.qr}>
        <PdfImageFromAzureWithOptions
          style={Styles.imageFit}
          fileName={themeData.qr_code_src}
          folderPath="assets/theme_qrs/"
        />
      </View>
    </InstagramPage>
  );
};

const Instagram05 = ({
  isCoBranded,
  coBrand,
  primaryBrand,
  lbBrand,
  themeData,
  uuid,
}: InstagramType): JSX.Element => {
  const primaryLogoView: LogoViewType = {
    width: 555,
    height: 164.5,
    position: 'absolute',
    left: 36,
    top: 190,
  };
  const primaryLogoDimensions: RectangularDimensions = 
    getLogoDimensionsForView(
      {
        width: primaryBrand.logoWidth,
        height: primaryBrand.logoHeight,
      },
      {
        width: primaryLogoView.width,
        height: primaryLogoView.height,
      },
    );
  const coBrandLogoView: LogoViewType = {
    width: 259,
    height: 82,
    position: 'absolute',
    left: 36,
    bottom: 118,
  };
  const coBrandLogoDimensions: RectangularDimensions = 
    getLogoDimensionsForView(
      {
        width: coBrand.logoWidth,
        height: coBrand.logoHeight,
      },{
        width: coBrandLogoView.width,
        height: coBrandLogoView.height,
      },
    );
  const lbLogoView: LogoViewType = {
    width: 259,
    height: 82,
    position: 'absolute',
    left: 331.5,
    bottom: 118,
  };
  const lbLogoDimensions: RectangularDimensions =
    getLogoDimensionsForView({
      width: 500,
      height: 125,
    }, {
      width: lbLogoView.width,
      height: lbLogoView.height,
    },
  );

  return (
    primaryLogoDimensions &&
      <InstagramPage
        style={{
          color: primaryBrand.colors.dark,
        }}
      >
        <View style={Styles.announcementView}>
          <Text style={Styles.announcement}>An announcement about</Text>
          <Text style={Styles.announcementTheme}>{themeData.public_name}</Text>
        </View>
        <View style={Styles.brandView}>
          <View style={Styles.brandLogo}>
            <PdfImageFromAzureWithUri
              style={primaryLogoDimensions}
              fileUri={primaryBrand.logoUrl}
            />
          </View>
        </View>
        {
          isCoBranded && coBrand &&
            <>
              <View style={coBrandLogoView}>
                <PdfImageFromAzureWithUri
                  style={coBrandLogoDimensions}
                  fileUri={formatImageSource(coBrand.logoUrl)}
                />
              </View>
              <View style={lbLogoView}>
                <PdfImageFromAzureWithUri
                  style={lbLogoDimensions}
                  fileUri={formatImageSource(lbBrand.logoUrl)}
                />
              </View>
            </>
        }
        <View style={Styles.materialIdView}>
          <Text style={Styles.materialId}>
            {/* {materialId()} */}
            {uuid}
          </Text>
        </View>
      </InstagramPage>
  );
};

const Instagram = ({
  isCoBranded,
  coBrand,
  primaryBrand,
  lbBrand,
  imgSrc,
  themeData,
  radius,
  zipCode,
  uuid,
}): JSX.Element => {

  Styles.photo.backgroundColor = primaryBrand.colors.primary;

  return (
    <ErrorBoundary>
      <Instagram01 themeData={themeData} radius={radius} zipCode={zipCode} imgSrc={imgSrc} primaryBrand={primaryBrand} />
      <Instagram02 themeData={themeData} radius={radius} zipCode={zipCode} primaryBrand={primaryBrand} />
      <Instagram03 themeData={themeData} radius={radius} zipCode={zipCode} primaryBrand={primaryBrand} />
      <Instagram04 themeData={themeData} primaryBrand={primaryBrand} />
      <Instagram05 themeData={themeData} primaryBrand={primaryBrand} uuid={uuid} isCoBranded={isCoBranded} coBrand={coBrand} lbBrand={lbBrand} />
    </ErrorBoundary>
  );
};

export default Instagram;
